import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  SubmissionLog, 
  SubmissionLaneType, 
  SubmissionStatus, 
  SubmissionDetails,
  PayerSubmissionConfig,
  DEFAULT_PORTAL_CHECKLIST,
} from '@/lib/submission/types';
import { 
  buildPASRequestPayload, 
  submitPASRequest, 
  parsePASResponse,
  isPASAvailable,
} from '@/lib/submission/pasConnector';
import { AuthorizationRequest } from '@/lib/authorization/types';
import { Json } from '@/integrations/supabase/types';

interface UseSubmissionLaneOptions {
  authorizationId: string | null;
  payerId?: string;
  planId?: string;
}

export function useSubmissionLane({ authorizationId, payerId, planId }: UseSubmissionLaneOptions) {
  const [submissionLogs, setSubmissionLogs] = useState<SubmissionLog[]>([]);
  const [config, setConfig] = useState<PayerSubmissionConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchSubmissionLogs = useCallback(async () => {
    if (!authorizationId) {
      setSubmissionLogs([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('submission_logs')
        .select('*')
        .eq('authorization_id', authorizationId)
        .order('executed_at', { ascending: false });

      if (fetchError) throw fetchError;
      
      // Map database results to our types
      setSubmissionLogs((data || []).map(log => ({
        ...log,
        lane: log.lane as SubmissionLaneType,
        status: log.status as SubmissionStatus,
        submission_details: log.submission_details as unknown as SubmissionDetails | undefined,
        raw_request_payload: log.raw_request_payload as unknown as Record<string, unknown> | undefined,
        raw_response_payload: log.raw_response_payload as unknown as Record<string, unknown> | undefined,
      })));
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [authorizationId]);

  const fetchConfig = useCallback(async () => {
    if (!payerId) {
      setConfig(null);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('payer_submission_config')
        .select('*')
        .eq('payer_id', payerId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      
      if (data) {
        setConfig({
          ...data,
          preferred_lane: data.preferred_lane as SubmissionLaneType,
          portal_required_fields: data.portal_required_fields as unknown as PayerSubmissionConfig['portal_required_fields'],
        });
      }
    } catch (err) {
      console.error('Error fetching submission config:', err);
    }
  }, [payerId]);

  useEffect(() => {
    fetchSubmissionLogs();
    fetchConfig();
  }, [fetchSubmissionLogs, fetchConfig]);

  const createSubmissionLog = async (
    lane: SubmissionLaneType,
    details?: SubmissionDetails
  ): Promise<string | null> => {
    if (!authorizationId) return null;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error: insertError } = await supabase
        .from('submission_logs')
        .insert([{
          authorization_id: authorizationId,
          lane,
          executed_by: user?.id,
          submission_details: (details || {}) as unknown as Json,
          status: 'PENDING',
        }])
        .select()
        .single();

      if (insertError) throw insertError;
      
      await fetchSubmissionLogs();
      return data?.id || null;
    } catch (err) {
      setError(err as Error);
      return null;
    }
  };

  const updateSubmissionLog = async (
    logId: string,
    updates: Partial<{
      status: SubmissionStatus;
      submission_reference_id: string;
      payer_confirmation_number: string;
      submission_details: SubmissionDetails;
      proof_file_path: string;
      proof_url: string;
      error_message: string;
      submitted_at: string;
      confirmed_at: string;
      raw_request_payload: Record<string, unknown>;
      raw_response_payload: Record<string, unknown>;
    }>
  ): Promise<boolean> => {
    try {
      // Convert to database-compatible types
      const dbUpdates: Record<string, unknown> = { ...updates };
      if (updates.submission_details) {
        dbUpdates.submission_details = updates.submission_details as unknown as Json;
      }
      if (updates.raw_request_payload) {
        dbUpdates.raw_request_payload = updates.raw_request_payload as unknown as Json;
      }
      if (updates.raw_response_payload) {
        dbUpdates.raw_response_payload = updates.raw_response_payload as unknown as Json;
      }
      
      const { error: updateError } = await supabase
        .from('submission_logs')
        .update(dbUpdates)
        .eq('id', logId);

      if (updateError) throw updateError;
      
      await fetchSubmissionLogs();
      
      // If confirmed, update the authorization_requests table
      if (updates.status === 'CONFIRMED' && authorizationId) {
        await supabase
          .from('authorization_requests')
          .update({
            submission_reference_id: updates.submission_reference_id || updates.payer_confirmation_number,
            submission_proof_path: updates.proof_file_path,
            last_submission_at: new Date().toISOString(),
          })
          .eq('id', authorizationId);
      }
      
      return true;
    } catch (err) {
      setError(err as Error);
      return false;
    }
  };

  const submitViaPAS = async (
    caseData: AuthorizationRequest,
    endpoint: string,
    accessToken?: string
  ): Promise<{ success: boolean; logId?: string; error?: string }> => {
    if (!authorizationId) return { success: false, error: 'No authorization ID' };
    
    setSubmitting(true);
    
    try {
      // Create log entry
      const logId = await createSubmissionLog('FHIR_PAS', {
        pas_endpoint: endpoint,
      });
      
      if (!logId) throw new Error('Failed to create submission log');
      
      // Build PAS request
      const payload = buildPASRequestPayload(
        caseData,
        caseData.patient ? {
          mrn: caseData.patient.mrn,
          first_name: caseData.patient.first_name,
          last_name: caseData.patient.last_name,
          dob: caseData.patient.dob,
        } : undefined,
        caseData.payer ? {
          name: caseData.payer.name,
          payer_id: caseData.payer.payer_id,
        } : undefined
      );
      
      // Store request payload
      await updateSubmissionLog(logId, {
        raw_request_payload: payload as unknown as Record<string, unknown>,
        status: 'SUBMITTED',
        submitted_at: new Date().toISOString(),
      });
      
      // Submit to PAS endpoint
      const result = await submitPASRequest(endpoint, payload, accessToken);
      
      if (!result.success) {
        await updateSubmissionLog(logId, {
          status: 'FAILED',
          error_message: result.error,
        });
        return { success: false, logId, error: result.error };
      }
      
      // Parse response and update log
      const parsed = parsePASResponse(result.response!);
      
      await updateSubmissionLog(logId, {
        status: 'CONFIRMED',
        raw_response_payload: result.response as unknown as Record<string, unknown>,
        submission_reference_id: parsed.auth_number,
        confirmed_at: new Date().toISOString(),
      });
      
      // Update authorization request with PAS response
      const authUpdates: Record<string, unknown> = {
        payer_normalized_status: parsed.normalized_status,
        active_submission_lane: 'FHIR_PAS',
      };
      
      if (parsed.auth_number) {
        authUpdates.auth_number = parsed.auth_number;
      }
      if (parsed.effective_start) {
        authUpdates.auth_effective_start = parsed.effective_start;
      }
      if (parsed.effective_end) {
        authUpdates.auth_effective_end = parsed.effective_end;
      }
      if (parsed.denial_reason) {
        authUpdates.denial_reason = parsed.denial_reason;
      }
      
      await supabase
        .from('authorization_requests')
        .update(authUpdates)
        .eq('id', authorizationId);
      
      return { success: true, logId };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    } finally {
      setSubmitting(false);
    }
  };

  const startPortalSubmission = async (): Promise<string | null> => {
    const details: SubmissionDetails = {
      portal_url: config?.portal_url,
      checklist_items: [...DEFAULT_PORTAL_CHECKLIST],
    };
    
    const logId = await createSubmissionLog('PORTAL', details);
    
    // Update authorization with active lane
    if (logId && authorizationId) {
      await supabase
        .from('authorization_requests')
        .update({ active_submission_lane: 'PORTAL' })
        .eq('id', authorizationId);
    }
    
    return logId;
  };

  const startFaxPhoneSubmission = async (
    type: 'fax' | 'phone',
    contactInfo: { fax_number?: string; phone_number?: string; contact_name?: string }
  ): Promise<string | null> => {
    const details: SubmissionDetails = {
      ...contactInfo,
    };
    
    const logId = await createSubmissionLog('FAX_PHONE', details);
    
    // Update authorization with active lane
    if (logId && authorizationId) {
      await supabase
        .from('authorization_requests')
        .update({ active_submission_lane: 'FAX_PHONE' })
        .eq('id', authorizationId);
    }
    
    return logId;
  };

  const confirmSubmission = async (
    logId: string,
    confirmationData: {
      reference_id?: string;
      confirmation_number?: string;
      proof_file_path?: string;
      proof_url?: string;
      notes?: string;
    }
  ): Promise<boolean> => {
    const success = await updateSubmissionLog(logId, {
      status: 'CONFIRMED',
      submission_reference_id: confirmationData.reference_id,
      payer_confirmation_number: confirmationData.confirmation_number,
      proof_file_path: confirmationData.proof_file_path,
      proof_url: confirmationData.proof_url,
      confirmed_at: new Date().toISOString(),
      submission_details: confirmationData.notes ? { notes: confirmationData.notes } : undefined,
    });
    
    // Update authorization status to submitted
    if (success && authorizationId) {
      await supabase
        .from('authorization_requests')
        .update({
          status: 'submitted_to_payer',
          submitted_to_payer_at: new Date().toISOString(),
          payer_reference_number: confirmationData.confirmation_number || confirmationData.reference_id,
        })
        .eq('id', authorizationId);
    }
    
    return success;
  };

  const retrySubmission = async (logId: string): Promise<boolean> => {
    return updateSubmissionLog(logId, {
      status: 'RETRYING',
    });
  };

  return {
    submissionLogs,
    config,
    loading,
    submitting,
    error,
    refetch: fetchSubmissionLogs,
    createSubmissionLog,
    updateSubmissionLog,
    submitViaPAS,
    startPortalSubmission,
    startFaxPhoneSubmission,
    confirmSubmission,
    retrySubmission,
    isPASAvailable: () => isPASAvailable(config || undefined),
  };
}
